import { $logger } from '@/boot/logger'
import {
  findSlugInPath,
  isAccountSetupWhitelistPath,
  isBlacklistPath,
  getPartnerSlugFromPath,
  toAndFromPathsAreEqual,
  serializeRoute,
} from '@/utils/routes.util'

export default function () {
  const $q = useQuasar()
  const route = useRoute()
  const router = useRouter()
  const navigationStore = useNavigationStore()
  const sessionStore = useSessionStore()
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)
  const { sessionIsReady, sessionIsAuthed, sessionIsPreviewMode, sessionPhoneAuthIsComplete } = storeToRefs(sessionStore)
  const accountSetupStore = useAccountSetupStore()
  const { accountSetupIsDone } = storeToRefs(accountSetupStore)
  const { featureFlags } = useFeatureFlagStore()
  // const toRoute = ref(null)

  // handle preview mode changes
  watch(
    sessionIsPreviewMode,
    (curr, prev) => {
      if (curr && !prev) {
        router.replace({ name: 'Home' })
        // force app to re-render when lockdown or preview mode changes
        sessionStore.refreshSessionKey()
      }
    },
    { immediate: true }
  )

  // Watch for maintenance mode changes and redirect users accordingly
  watch(
    () => featureFlags.maintenanceMode?.enabled,
    (curr, prev) => {
      if (curr && !prev) {
        // Maintenance mode turned ON - redirect to maintenance page
        $logger.debug('NAV GUARD: Maintenance mode enabled, redirecting to maintenance page')
        router.push({ name: 'MaintenancePage' })
      } else if (!curr && prev) {
        // Maintenance mode turned OFF - redirect away from maintenance page
        if (route.name === 'MaintenancePage') {
          $logger.debug('NAV GUARD: Maintenance mode disabled, redirecting away from maintenance page')
          // go to previous route
          router.replace({ name: 'Home' })
        }
      }
    },
    { immediate: true }
  )

  async function runBeforeEachGuards(to, from) {
    // Use existing refTo if present; otherwise rebuild target from path + query + hash
    // while excluding refTo itself (prevents recursive refTo encoding)
    const { refTo: _, ...otherQuery } = to.query || {}
    const qs = new URLSearchParams(otherQuery).toString()
    const target = `${to.path}${qs ? `?${qs}` : ''}${to.hash || ''}`
    const refTo = to.query?.refTo || encodeURI(target)

    // handle maintenance mode
    // IMPORTANT: this must exist in addition to the watch() above, to handle initial page loads
    if (!featureFlags.maintenanceMode?.enabled && to.name === 'MaintenancePage') {
      $logger.debug('NAV GUARD: MAINTENANCE MODE OFF - REDIRECT FROM MAINTENANCE PAGE')
      // if maintenance mode is off, prevent access to maintenance page
      return { name: 'Home' }
    }

    // NOTE: /app route blocking for non-native platforms is handled in router/guards/appRouteGuard.js
    // (registered at router level so it runs on initial navigation too)

    await sessionStore.awaitSessionInit()

    let prefix = ''

    // handle for native mobile paths
    if ((from.path.startsWith('/app') || $q.platform.is.nativeMobile) && !to.path.startsWith('/app')) {
      prefix = '/app'
    }

    const nextPath = !to.path.startsWith('/app') ? `${prefix}${to.path}` : to.path

    // must ensure auth is initialized before proceeding
    // await sessionStore.awaitSessionInit()

    /* ----- BLACKLISTED ROUTES ----- */
    // routes are blocked outright if they are in the blacklist
    if (isBlacklistPath(nextPath)) {
      $logger.debug('NAV GUARD: BLACKLISTED ROUTE', nextPath)
      return `${prefix}/`
    }

    /* ----- PARTNER EXPERIENCE ----- */
    // use full path so params like `continueUrl` are included
    if (/^\/(app\/)?p\/?.*/.test(to.path)) {
      const partnerSlug = getPartnerSlugFromPath(to.path)
      if (partnerSlug) {
        $logger.debug('PARTNER EXP ROUTE', partnerSlug)

        sessionStore.setPartnerExpSlug(partnerSlug)
      }
    } else if (!featureFlags.partnerExpForce?.enabled) {
      // clear any existing partner exp slug
      sessionStore.clearPartnerExpSlug()
    }

    to.meta = to.meta || {}
    to.meta.previous = from

    // add class to body if provided - used for customized styling and behavior
    if (to.meta?.bodyClass) {
      if (!Array.isArray(to.meta.bodyClass)) {
        to.meta.bodyClass = [to.meta.bodyClass]
      }
      to.meta.bodyClass.forEach(c => document.body.classList.add(c))
    }

    /* ----- SESSION GUARDS ----- */
    if (sessionIsReady.value) {
      // PROTECTED ROUTES
      if (to.meta?.protected && !sessionIsAuthed.value) {
        if (!prefix && nextPath.startsWith('/app')) {
          prefix = '/app'
        }

        $logger.debug('NAV GUARD: REDIRECT TO AUTH', prefix)
        return { path: `${prefix}/auth/`, query: { refTo } }
      }

      // prevent /shop route in preview mode
      if (sessionIsPreviewMode.value && findSlugInPath(nextPath, 'shop')) {
        $logger.debug('NAV GUARD: BLOCKED /shop ROUTE IN PREVIEW MODE', nextPath)
        return `${prefix}/`
      }

      /* ----- AUTHENTICATED GUARDS ----- */
      if (sessionIsAuthed.value) {
        // always allow sign out
        if (to.name === 'AuthSignOut') {
          return
        }

        // block users with pending account deletion
        if (user.value?.status === 'PENDING_DELETION') {
          if (['AccountDeleteInProgress', 'AccountDeleteConfirmed'].includes(to.name)) {
            return
          }
          $logger.debug('NAV GUARD: REDIRECT TO DELETION IN PROGRESS')
          return { path: `${prefix}/account/delete/in-progress` }
        }

        // redirect away from deletion pages if no longer pending deletion
        if (['AccountDeleteInProgress', 'AccountDeleteConfirmed'].includes(to.name)) {
          $logger.debug('NAV GUARD: NOT PENDING DELETION, REDIRECT TO ACCOUNT')
          return { name: 'AccountHome' }
        }

        // ensure phone auth is fully established
        if (!sessionPhoneAuthIsComplete.value) {
          // handle return flow differently
          if (nextPath.startsWith('/app/return')) {
            if (to.name !== 'AppReturnLookupOrder') {
              $logger.debug('NAV GUARD: REDIRECT TO RETURN AUTH')
              // send any return route back to order lookup
              return { name: 'AppReturnLookupOrder' }
            }

            // allow to proceed to current return route
            return
          }

          if (to.name !== 'AuthHome') {
            $logger.debug('NAV GUARD: REDIRECT TO PHONE AUTH')
            return { path: `${prefix}/auth`, query: { refTo } }
          }

          return
        }

        if (!accountSetupIsDone.value && !isAccountSetupWhitelistPath(nextPath)) {
          // always redirect to setup page if not completed
          $logger.debug('NAV GUARD: REDIRECT TO SETUP')
          return { path: `${prefix}/account/setup`, query: { refTo } }
        }

        if (to.name === 'AuthHome' && !to.query?.refTo) {
          $logger.debug('NAV GUARD: REDIRECT TO ROOT')
          return `${prefix}/`
        }

        // redirect from account setup if already completed
        if (accountSetupIsDone.value && to.name === 'AccountSetup') {
          $logger.debug('NAV GUARD: REDIRECT TO ACCOUNT')
          return to.query?.refTo || `${prefix}/account`
        }
      }
    }

    /* ----- NATIVE APP NAV HISTORY & ROUTING ----- */
    if ($q.platform.is.nativeMobile) {
      const toBaseRoute = /^\/app(\/)?$/.test(nextPath)
      const splashReturnRegExp = new RegExp('^/app/(splash|return)')
      const fromSplashReturnRoute = splashReturnRegExp.test(from.path)
      const toSplashReturnRoute = splashReturnRegExp.test(nextPath)

      if (fromSplashReturnRoute || toSplashReturnRoute || toBaseRoute) {
        navigationStore.clearHistory()
        if (fromSplashReturnRoute) {
          navigationStore.appendHistory(nextPath)
        }
      } else if (nextPath === navigationStore.previousRoute) {
        navigationStore.popHistory()
      } else if (nextPath !== navigationStore.currentRoute) {
        navigationStore.appendHistory(nextPath)
      }
    }

    if (to.path === nextPath) {
      $logger.debug('NAV GUARD: GO TO NEXT ROUTE', { from: serializeRoute(from), to: serializeRoute(to) })
      return
    }

    $logger.debug('NAV GUARD: REDIRECT TO NEXT PATH', nextPath, to.query, to.hash)
    return { query: to.query, hash: to.hash, params: to.params, path: nextPath }
  }

  // navigation guards
  // ⚠️ IMPORTANT: There are also redirect rules in `redirect.middleware.js`, which mimic server-side redirects
  router.beforeEach((to, from) => {
    $logger.debug('ROUTER - beforeEach()', { from: serializeRoute(from), to: serializeRoute(to) })
    // don't run if only hash or query is changing
    if (toAndFromPathsAreEqual(to, from)) {
      return
    } else {
      return runBeforeEachGuards(to, from)
    }
  })

  // remove trailing slashes on all paths except root
  router.beforeEach(to => {
    if (to.path !== '/' && to.path.endsWith('/')) {
      $logger.debug('NAV GUARD: REMOVE TRAILING SLASH', to.path)
      return { ...to, path: to.path.slice(0, -1) }
    }
  })

  // cleanup after each navigation
  router.afterEach((_to, from) => {
    // custom body class cleanup
    if (from.meta?.bodyClass) {
      if (!Array.isArray(from.meta.bodyClass)) {
        from.meta.bodyClass = [from.meta.bodyClass]
      }
      from.meta.bodyClass.forEach(c => document.body.classList.remove(c))
    }
  })
}
